import { throwIfLegalAcceptanceRequired } from "./legal_acceptance.js"
import {
  OFFLINE_CHUNK_SIZE,
  OfflinePdfStore,
  chunkPlan,
  offlinePdfKey
} from "./offline_pdf_store.js"

const MINIMUM_QUOTA_HEADROOM = 32 * 1024 * 1024
const DOWNLOAD_CONCURRENCY = 2

export class OfflinePdfError extends Error {
  constructor(code, message) {
    super(message)
    this.name = "OfflinePdfError"
    this.code = code
  }
}

export function validateManifest(manifest) {
  const edition = manifest?.edition
  if (manifest?.version !== 1 || !Number.isSafeInteger(manifest.user_id) || !Number.isSafeInteger(edition?.id)) {
    throw new OfflinePdfError("manifest", "Invalid offline manifest identity")
  }
  if (!edition.checksum || !Number.isSafeInteger(edition.byte_size) || edition.byte_size <= 0) {
    throw new OfflinePdfError("manifest", "Invalid offline manifest document")
  }
  if (!manifest.document_url || !manifest.reader_url) throw new OfflinePdfError("manifest", "Invalid offline manifest URLs")
  return manifest
}

export function validateHeadResponse(response, manifest) {
  if (!response.ok) throw new OfflinePdfError("authorization", `PDF metadata request returned ${response.status}`)
  if (response.headers.get("Accept-Ranges") !== "bytes") throw new OfflinePdfError("response", "PDF byte ranges are unavailable")
  if (response.headers.get("Content-Type")?.split(";", 1)[0] !== "application/pdf") {
    throw new OfflinePdfError("response", "PDF metadata returned an unexpected content type")
  }

  const length = Number(response.headers.get("Content-Length"))
  if (length !== manifest.edition.byte_size) throw new OfflinePdfError("stale", "PDF length no longer matches the manifest")
  const etag = response.headers.get("ETag")
  if (!etag) throw new OfflinePdfError("response", "PDF metadata is missing an ETag")
  return { length, etag }
}

export function validateChunkResponse(response, { begin, end, byteSize, etag }) {
  if (response.status === 412) throw new OfflinePdfError("stale", "PDF ETag changed during download")
  if (response.status !== 206) throw new OfflinePdfError("response", `PDF range request returned ${response.status}`)
  if (response.headers.get("Content-Range") !== `bytes ${begin}-${end - 1}/${byteSize}`) {
    throw new OfflinePdfError("response", "PDF range response does not match the requested bytes")
  }
  if (Number(response.headers.get("Content-Length")) !== end - begin) {
    throw new OfflinePdfError("response", "PDF range response has an unexpected length")
  }
  if (response.headers.get("ETag") !== etag) throw new OfflinePdfError("stale", "PDF ETag changed during download")
}

export function quotaAvailable({ usage, quota }, byteSize) {
  if (!Number.isFinite(usage) || !Number.isFinite(quota)) return false
  const headroom = Math.max(MINIMUM_QUOTA_HEADROOM, Math.ceil(byteSize * 0.1))
  return quota - usage >= byteSize + headroom
}

export function recordFromManifest(manifest, etag, manifestUrl, existing = null) {
  const record = {
    key: offlinePdfKey({ userId: manifest.user_id, editionId: manifest.edition.id, checksum: manifest.edition.checksum }),
    version: manifest.version,
    userId: manifest.user_id,
    book: manifest.book,
    editionId: manifest.edition.id,
    checksum: manifest.edition.checksum,
    byteSize: manifest.edition.byte_size,
    pageCount: manifest.edition.page_count,
    readerUrl: manifest.reader_url,
    documentUrl: manifest.document_url,
    manifestUrl,
    progressUrl: manifest.reader_url,
    expiresAt: manifest.expires_at,
    etag,
    chunkSize: OFFLINE_CHUNK_SIZE,
    completedChunks: existing?.completedChunks || [],
    ready: existing?.ready || false,
    progress: existing?.progress || manifest.progress,
    pendingProgress: existing?.pendingProgress || null,
    zoom: existing?.zoom || null,
    updatedAt: new Date().toISOString()
  }
  return record
}

export class OfflinePdfDownloader {
  constructor({ store = new OfflinePdfStore(), fetch = globalThis.fetch, storage = globalThis.navigator?.storage } = {}) {
    this.store = store
    this.fetch = fetch.bind(globalThis)
    this.storage = storage
  }

  async inspect(manifestUrl, { signal } = {}) {
    const manifestResponse = await this.fetch(manifestUrl, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal
    })
    await throwIfLegalAcceptanceRequired(manifestResponse)
    if (!manifestResponse.ok) throw new OfflinePdfError("authorization", `Offline manifest request returned ${manifestResponse.status}`)
    const manifest = validateManifest(await manifestResponse.json())

    const headResponse = await this.fetch(manifest.document_url, {
      method: "HEAD",
      credentials: "same-origin",
      headers: { Accept: "application/pdf" },
      signal
    })
    await throwIfLegalAcceptanceRequired(headResponse)
    const { etag } = validateHeadResponse(headResponse, manifest)
    return { manifest, etag }
  }

  async download(manifestUrl, { signal, onProgress = () => {}, inspection = null } = {}) {
    if (!this.store.supported({ write: true })) throw new OfflinePdfError("unsupported", "Writable OPFS is unavailable")

    const { manifest, etag } = inspection || await this.inspect(manifestUrl, { signal })
    const key = offlinePdfKey({ userId: manifest.user_id, editionId: manifest.edition.id, checksum: manifest.edition.checksum })
    let existing = await this.store.get(key)
    if (existing && existing.etag !== etag) {
      await this.store.delete(existing)
      existing = null
    }

    const record = recordFromManifest(manifest, etag, manifestUrl, existing)
    await this.store.deleteOtherEditions(record)
    await this.store.setActiveUser(record.userId)

    record.completedChunks = await this.store.completedChunks(record)
    const plan = chunkPlan(record.byteSize, record.chunkSize)
    const completed = new Set(record.completedChunks)
    const missing = plan.filter((chunk) => !completed.has(chunk.index))
    const remainingBytes = missing.reduce((sum, chunk) => sum + chunk.end - chunk.begin, 0)
    await this.storage.persist?.()
    const estimate = await this.storage.estimate()
    if (remainingBytes > 0 && !quotaAvailable(estimate, remainingBytes)) {
      await this.store.put(record)
      throw new OfflinePdfError("quota", "Insufficient storage quota")
    }

    record.ready = false
    await this.store.put(record)

    onProgress({ completed: completed.size, total: plan.length, record })

    const workerController = new AbortController()
    const stopWorkers = () => workerController.abort(signal?.reason)
    if (signal?.aborted) stopWorkers()
    signal?.addEventListener("abort", stopWorkers, { once: true })

    try {
      await this.runWorkers(missing, DOWNLOAD_CONCURRENCY, async (chunk) => {
        const response = await this.fetch(record.documentUrl, {
          method: "GET",
          credentials: "same-origin",
          headers: {
            Accept: "application/pdf",
            Range: `bytes=${chunk.begin}-${chunk.end - 1}`,
            "If-Range": record.etag
          },
          signal: workerController.signal
        })
        await throwIfLegalAcceptanceRequired(response)
        validateChunkResponse(response, { ...chunk, byteSize: record.byteSize, etag: record.etag })
        const bytes = new Uint8Array(await response.arrayBuffer())
        if (workerController.signal.aborted) throw workerController.signal.reason
        if (bytes.byteLength !== chunk.end - chunk.begin) {
          throw new OfflinePdfError("response", "PDF range body has an unexpected length")
        }
        await this.store.writeChunk(record, chunk.index, bytes)
        completed.add(chunk.index)
        record.completedChunks = [...completed].sort((left, right) => left - right)
        record.updatedAt = new Date().toISOString()
        await this.store.put(record)
        onProgress({ completed: completed.size, total: plan.length, record })
      }, { onError: stopWorkers })
    } catch (error) {
      if (error.code === "stale") await this.store.delete(record)
      throw error
    } finally {
      signal?.removeEventListener("abort", stopWorkers)
    }

    if (!await this.store.verifyComplete(record)) {
      record.completedChunks = await this.store.completedChunks(record)
      await this.store.put(record)
      throw new OfflinePdfError("incomplete", "Offline PDF chunks are incomplete")
    }

    record.completedChunks = chunkPlan(record.byteSize, record.chunkSize).map(({ index }) => index)
    record.updatedAt = new Date().toISOString()
    const readyState = await this.store.markReadyIfActive(record)
    Object.assign(record, readyState.record)
    if (!readyState.active) {
      try {
        await this.store.delete(record)
      } finally {
        throw new OfflinePdfError("superseded", "A newer edition was opened during the download")
      }
    }
    return record
  }

  async runWorkers(items, concurrency, worker, { onError = () => {} } = {}) {
    const queue = [...items]
    let failure = null
    await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
      while (!failure && queue.length) {
        try {
          await worker(queue.shift())
        } catch (error) {
          if (!failure) {
            failure = error
            onError(error)
          }
        }
      }
    }))
    if (failure) throw failure
  }
}
